// PrintService.ts — Payroll module (web)
//
// Same design language as the Award Role result-sheet PrintService: a single
// color palette drives every header/table/badge color, 'color' mode
// reproduces the blue-gradient Payroll UI, 'bw' is a genuine
// white-bg/black-text/black-border print style for cheap black-ink printers.
//
// Two documents are covered:
//   - buildPayslipHtml           → one employee's salary slip
//   - buildBulkPayslipsHtml      → many salary slips, one per printed page
//   - buildPayrollRegisterHtml   → the tabular payroll register (the on-screen
//                                  records table, formatted for print)
//
// window.open()+print() is used exactly like the Award Role print flow —
// deliberately no win.close() call, since closing the tab automatically cuts
// the print dialog off before the user can finish/cancel it.

import type { PayrollRecord } from '../PayrollRecords';

export type Orientation = 'portrait' | 'landscape';
export type PaperSize = 'A4' | 'A3';
export type ColorMode = 'color' | 'bw';

export interface PrintDisplayOptions {
  colorMode?: ColorMode;
}

const resolveDisplayOptions = (opts?: PrintDisplayOptions): { colorMode: ColorMode } => ({
  colorMode: opts?.colorMode ?? 'color',
});

// ── Status → color mapping (mirrors StatusBadge in PayrollRecords.tsx) ──────

const STATUS_META: Record<PayrollRecord['payment_status'], { label: string; color: string }> = {
  paid:      { label: 'Paid',      color: '#16A34A' },
  pending:   { label: 'Pending',   color: '#D97706' },
  on_hold:   { label: 'On Hold',   color: '#4338CA' },
  cancelled: { label: 'Cancelled', color: '#DC2626' },
};

// ── Color-mode palette ──────────────────────────────────────────────────────
// Every banner/header/table-head color in the document is driven from this
// single palette. 'color' reproduces the blue-gradient Payroll design;
// 'bw' is a genuine white-bg/black-text/black-border print style — not just
// a flattened, still-blue version of the same design.

interface Palette {
  bandBg:            string;
  bandBorder:        string;
  textPrimary:       string;
  textSecondary:     string;
  textMuted:         string;
  chipBg:            string;
  chipBorder:        string;
  dividerColor:      string;
  theadBg:           string;
  theadText:         string;
  theadBottomBorder: string;
  tableBorder:       string;
  tableShadow:       string;
}

const getPalette = (colorMode: ColorMode): Palette => {
  if (colorMode === 'bw') {
    return {
      bandBg: '#FFFFFF',
      bandBorder: '2px solid #111827',
      textPrimary: '#111827',
      textSecondary: '#4B5563',
      textMuted: '#6B7280',
      chipBg: '#F3F4F6',
      chipBorder: '1px solid #9CA3AF',
      dividerColor: '#9CA3AF',
      theadBg: '#FFFFFF',
      theadText: '#111827',
      theadBottomBorder: '2px solid #111827',
      tableBorder: '#898B8D',
      tableShadow: 'none',
    };
  }
  return {
    bandBg: 'linear-gradient(135deg,#1e40af 0%,#3b82f6 55%,#60a5fa 100%)',
    bandBorder: 'none',
    textPrimary: '#ffffff',
    textSecondary: 'rgba(255,255,255,0.65)',
    textMuted: 'rgba(255,255,255,0.6)',
    chipBg: 'rgba(255,255,255,0.18)',
    chipBorder: '1px solid rgba(255,255,255,0.25)',
    dividerColor: 'rgba(255,255,255,0.18)',
    theadBg: 'linear-gradient(135deg,#1e40af,#3b82f6)',
    theadText: 'rgba(255,255,255,0.85)',
    theadBottomBorder: 'none',
    tableBorder: '#E5E7EB',
    tableShadow: '0 2px 10px rgba(0,0,0,0.06)',
  };
};

// In B&W mode, colored badges/text are neutralized to grayscale-safe tones so
// nothing relies on a color channel a black-ink-only printer would drop.

const BW_INK = '#1F2937';
const BW_BORDER = '#6B7280';

const ink       = (hex: string, colorMode: ColorMode): string => (colorMode === 'bw' ? BW_INK : hex);
const inkBg     = (hex: string, colorMode: ColorMode): string => (colorMode === 'bw' ? 'transparent' : `${hex}18`);
const inkBorder = (hex: string, colorMode: ColorMode): string => (colorMode === 'bw' ? BW_BORDER : `${hex}40`);

const statusBadgeHtml = (status: PayrollRecord['payment_status'], colorMode: ColorMode): string => {
  const meta = STATUS_META[status] ?? STATUS_META.pending;
  const color = ink(meta.color, colorMode);
  return `<span style="display:inline-block;background:${inkBg(meta.color, colorMode)};color:${color};border:1px solid ${inkBorder(meta.color, colorMode)};padding:4px 12px;border-radius:20px;font-size:12px;font-weight:800;">${meta.label}</span>`;
};

// ── Small helpers ─────────────────────────────────────────────────────────

const escapeHtml = (s: string) =>
  String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const fmtCurrency = (n: number | string): string => {
  const num = Number(n) || 0;
  return `₹${num.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

const fmtDateTime = () =>
  new Date().toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });

const fmtDate = (d: string | null) => {
  if (!d) return '—';
  const parsed = new Date(d);
  if (Number.isNaN(parsed.getTime())) return d;
  return parsed.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const monthLabel = (monthYear: string): string => {
  const [y, m] = monthYear.split('-').map(Number);
  if (!y || !m) return monthYear;
  return new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
};

const getInitials = (name: string | null | undefined) =>
  (name ?? '?').trim().split(/\s+/).slice(0, 2).map((w) => w.charAt(0).toUpperCase()).join('');

const paymentModeLabel = (mode: PayrollRecord['payment_mode']): string => {
  if (!mode) return '—';
  return mode.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

// ── Shared print CSS ─────────────────────────────────────────────────────

const getBaseStyles = (colorMode: ColorMode): string => {
  const p = getPalette(colorMode);
  return `
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif;
     background:#F0F4FB;color:#0F1B3D;font-size:13px;
     -webkit-print-color-adjust:exact;print-color-adjust:exact}
.page{max-width:900px;margin:0 auto;padding:24px}
.page.wide{max-width:1400px}
@page{size:A4 portrait;margin:10mm}
.header{background:${p.bandBg};border:${p.bandBorder};
        border-radius:16px;padding:22px 26px;margin-bottom:20px;
        display:flex;justify-content:space-between;align-items:flex-start}
.hl{flex:1}.hr{text-align:right;flex-shrink:0;margin-left:20px}
.logo-row{display:flex;align-items:center;gap:12px;margin-bottom:10px}
.logo-box{width:44px;height:44px;background:${p.chipBg};border-radius:12px;
          display:flex;align-items:center;justify-content:center;font-size:24px;
          border:${p.chipBorder}}
.logo-name{font-size:22px;font-weight:900;color:${p.textPrimary};letter-spacing:-0.8px;line-height:1.1}
.logo-sub{font-size:9px;color:${p.textMuted};font-weight:700;letter-spacing:1.2px;text-transform:uppercase;margin-top:2px}
.divider{height:1px;background:${p.dividerColor};margin:10px 0}
.table-wrap{background:#fff;border-radius:14px;overflow:hidden;border:1px solid ${p.tableBorder};
            box-shadow:${p.tableShadow};margin-bottom:22px}
table{width:100%;border-collapse:collapse}
thead tr{background:${p.theadBg};border-bottom:${p.theadBottomBorder}}
thead th{padding:12px 10px;text-align:left;font-size:10px;font-weight:700;
         color:${p.theadText};letter-spacing:0.6px;text-transform:uppercase;white-space:nowrap;
         border-right:1px solid ${p.dividerColor}}
thead th:last-child{border-right:none}
thead th.c{text-align:center}
tbody td{padding:10px;border-bottom:1px solid ${p.tableBorder};border-right:1px solid ${p.tableBorder};
         vertical-align:middle;text-align:left}
tbody td.c{text-align:center}
tbody td:last-child{border-right:none}
tbody tr:last-child td{border-bottom:none}
.footer{text-align:center;color:#94A3B8;font-size:11px;padding:6px 0 10px}
.sc2{background:#fff;border:1px solid #E5E7EB;border-radius:12px;padding:14px 16px;box-shadow:0 2px 8px rgba(0,0,0,0.05)}
.sk{font-size:9px;font-weight:700;color:#9CA3AF;text-transform:uppercase;letter-spacing:1px}
.sv{font-size:18px;font-weight:800;margin-top:4px}
.sl{font-size:11px;color:#6B7280;margin-top:2px;font-weight:600}
.st{font-size:10px;font-weight:800;color:#6B7280;text-transform:uppercase;letter-spacing:1px;margin-bottom:10px;padding-bottom:6px;border-bottom:2px solid #E5E7EB}
.section{background:#fff;border:1px solid #E5E7EB;border-radius:12px;padding:16px 18px;margin-bottom:16px;box-shadow:0 2px 8px rgba(0,0,0,0.04)}
.kv{display:flex;justify-content:space-between;padding:6px 0;border-bottom:1px solid #F3F4F6}
.kv:last-child{border-bottom:none}
.kv .k{font-size:12px;color:#6B7280;font-weight:500}
.kv .v{font-size:12px;color:#111827;font-weight:700}
.kv.total .k{font-size:13px;color:#111827;font-weight:800}
.kv.total .v{font-size:15px;font-weight:900}
thead{display:table-header-group}
tbody tr{page-break-inside:avoid;break-inside:avoid}
@media print{
  body{background:#fff}.page{padding:10px;max-width:100%}
  tbody tr{page-break-inside:avoid;break-inside:avoid}
}`;
};

const buildLogoHtml = (schoolName: string | undefined, colorMode: ColorMode): string => {
  const p = getPalette(colorMode);
  return `
<div class="logo-row">
  <div class="logo-box">&#128176;</div>
  <div>
    <div class="logo-name" style="color:${p.textPrimary};">${schoolName ? escapeHtml(schoolName) : 'SkoolPillar'}</div>
    <div class="logo-sub" style="color:${p.textMuted};">${schoolName ? 'Powered by SkoolPillar ERP' : 'Payroll · ERP Management System'}</div>
  </div>
</div>
<div class="divider"></div>`;
};

// ── Print plumbing ───────────────────────────────────────────────────────

/** Overrides the @page rule with the chosen paper size + orientation. */
export const applyPrintOptions = (html: string, paper: PaperSize, orientation: Orientation): string => {
  const override = `<style>@page{size:${paper} ${orientation};margin:10mm}</style>`;
  return html.includes('</head>') ? html.replace('</head>', `${override}</head>`) : override + html;
};

/**
 * Opens a new tab with the given HTML and triggers the browser's print
 * dialog. Deliberately does NOT call win.close() — closing the window
 * programmatically races the print dialog and can cut it off before the
 * user finishes (or cancels) printing.
 */
export const printHtml = (html: string): void => {
  const win = window.open('', '_blank');
  if (!win) {
    window.alert('Please allow pop-ups for this site to print.');
    return;
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => win.print(), 350);
};

// ── Payslip body (shared between single + bulk builders) ────────────────

const buildPayslipBody = (
  record: PayrollRecord,
  className: string | undefined, // e.g. school/branch name shown under employee — optional, unused for now but kept for future class/dept context
  colorMode: ColorMode,
): string => {
  const p = getPalette(colorMode);
  const initials = getInitials(record.employee_name);
  const attendPct = record.working_days > 0 ? Math.round((record.present_days / record.working_days) * 100) : 0;
  const roleLabel = record.employee_role.charAt(0).toUpperCase() + record.employee_role.slice(1);

  return `
  <div class="header" style="margin-bottom:16px;">
    <div class="hl">
      ${buildLogoHtml(undefined, colorMode)}
      <div style="font-size:16px;font-weight:800;color:${p.textPrimary};">Salary Slip</div>
      <div style="font-size:11px;color:${p.textSecondary};margin-top:4px;">${monthLabel(record.month_year)}${record.session ? ` · Session ${escapeHtml(record.session)}` : ''}</div>
    </div>
    <div class="hr">
      <div style="font-size:11px;color:${p.textMuted};letter-spacing:.8px;text-transform:uppercase;font-weight:700;margin-bottom:6px;">Payment Status</div>
      ${statusBadgeHtml(record.payment_status, colorMode)}
    </div>
  </div>

  <div class="section" style="display:flex;align-items:center;gap:16px;">
    <div style="width:60px;height:60px;border-radius:14px;background:#EFF4FF;border:2px solid #C5D9FF;display:flex;align-items:center;justify-content:center;font-size:22px;font-weight:900;color:#2855AE;flex-shrink:0;">${initials}</div>
    <div style="flex:1;">
      <div style="font-size:18px;font-weight:900;color:#111827;letter-spacing:-0.3px;">${escapeHtml(record.employee_name ?? `#${record.employee_id}`)}</div>
      <div style="font-size:12px;color:#6B7280;margin-top:3px;font-weight:600;">${roleLabel} &nbsp;·&nbsp; Employee ID #${record.employee_code ?? record.employee_id}</div>
    </div>
    <div style="text-align:right;">
      <div class="sk">Net Pay</div>
      <div style="font-size:22px;font-weight:900;color:#2855AE;margin-top:2px;">${fmtCurrency(record.net_salary)}</div>
    </div>
  </div>

  <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;">
    <div class="section">
      <div class="st" style="color:#1D4ED8;border-bottom-color:#DBEAFE;">Earnings</div>
      <div class="kv"><span class="k">Basic Salary</span><span class="v">${fmtCurrency(record.basic_salary)}</span></div>
      <div class="kv"><span class="k">HRA</span><span class="v">${fmtCurrency(record.hra)}</span></div>
      <div class="kv"><span class="k">Transport Allowance</span><span class="v">${fmtCurrency(record.transport_allowance)}</span></div>
      <div class="kv"><span class="k">Medical Allowance</span><span class="v">${fmtCurrency(record.medical_allowance)}</span></div>
      <div class="kv"><span class="k">Other Allowance</span><span class="v">${fmtCurrency(record.other_allowance)}</span></div>
      <div class="kv total"><span class="k">Gross Salary</span><span class="v" style="color:${ink('#1D4ED8', colorMode)};">${fmtCurrency(record.gross_salary)}</span></div>
    </div>
    <div class="section">
      <div class="st" style="color:#DC2626;border-bottom-color:#FEE2E2;">Deductions</div>
      <div class="kv"><span class="k">Provident Fund</span><span class="v">${fmtCurrency(record.provident_fund)}</span></div>
      <div class="kv"><span class="k">Professional Tax</span><span class="v">${fmtCurrency(record.professional_tax)}</span></div>
      <div class="kv"><span class="k">TDS</span><span class="v">${fmtCurrency(record.tds)}</span></div>
      <div class="kv"><span class="k">Other Deductions</span><span class="v">${fmtCurrency(record.other_deductions)}</span></div>
      <div class="kv total"><span class="k">Total Deductions</span><span class="v" style="color:${ink('#DC2626', colorMode)};">${fmtCurrency(record.total_deductions)}</span></div>
    </div>
  </div>

  <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-bottom:16px;">
    <div class="sc2"><div class="sk">Working Days</div><div class="sv" style="color:#374151;">${record.working_days}</div></div>
    <div class="sc2"><div class="sk">Present Days</div><div class="sv" style="color:${ink('#16A34A', colorMode)};">${record.present_days}</div></div>
    <div class="sc2"><div class="sk">Leave Days</div><div class="sv" style="color:${ink('#DC2626', colorMode)};">${record.leave_days}</div></div>
    <div class="sc2"><div class="sk">Attendance</div><div class="sv" style="color:${ink(attendPct >= 75 ? '#16A34A' : '#DC2626', colorMode)};">${attendPct}%</div></div>
  </div>

  <div class="section" style="background:${p.chipBg === 'rgba(255,255,255,0.18)' ? '#EFF4FF' : '#F9FAFB'};border-color:${colorMode === 'bw' ? '#9CA3AF' : '#DBEAFE'};">
    <div class="st" style="border-bottom-color:${colorMode === 'bw' ? '#D1D5DB' : '#DBEAFE'};">Payment Details</div>
    <div class="kv"><span class="k">Payment Mode</span><span class="v">${paymentModeLabel(record.payment_mode)}</span></div>
    <div class="kv"><span class="k">Payment Date</span><span class="v">${fmtDate(record.payment_date)}</span></div>
    <div class="kv"><span class="k">Transaction / Cheque Ref.</span><span class="v">${record.transaction_ref ? escapeHtml(record.transaction_ref) : '—'}</span></div>
    ${record.remarks ? `<div class="kv"><span class="k">Remarks</span><span class="v">${escapeHtml(record.remarks)}</span></div>` : ''}
  </div>

  <div style="text-align:center;padding:16px 0 4px;">
    <div style="font-size:22px;font-weight:900;color:${ink('#2855AE', colorMode)};">Net Payable: ${fmtCurrency(record.net_salary)}</div>
    <div style="font-size:10px;color:#9CA3AF;margin-top:10px;">This is a computer-generated payslip and does not require a signature.</div>
  </div>`;
};

/** Single-employee salary slip — one full print document. */
export const buildPayslipHtml = (
  record: PayrollRecord,
  schoolName: string | undefined,
  displayOptions?: PrintDisplayOptions,
): string => {
  const { colorMode } = resolveDisplayOptions(displayOptions);
  const now = fmtDateTime();
  const title = `Salary Slip · ${record.employee_name ?? `#${record.employee_id}`} · ${monthLabel(record.month_year)}`;

  return `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"/><title>${title}</title><style>${getBaseStyles(colorMode)}</style></head>
<body><div class="page">
  ${buildPayslipBody(record, schoolName, colorMode)}
  <div class="footer">Generated: ${now}</div>
</div></body></html>`;
};

/** Many salary slips, one per printed page — for printing a whole month/role in one go. */
export const buildBulkPayslipsHtml = (
  records: PayrollRecord[],
  schoolName: string | undefined,
  displayOptions?: PrintDisplayOptions,
): string => {
  const { colorMode } = resolveDisplayOptions(displayOptions);
  const now = fmtDateTime();

  const pages = records.map((r, i) => `
    <div class="page" style="${i > 0 ? 'page-break-before:always;' : ''}">
      ${buildPayslipBody(r, schoolName, colorMode)}
      <div class="footer">Generated: ${now}</div>
    </div>`).join('');

  return `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"/><title>Salary Slips (${records.length})</title><style>${getBaseStyles(colorMode)}</style></head>
<body>${pages || '<div class="page"><div class="footer">No records selected</div></div>'}</body></html>`;
};

// ── Payroll register (the on-screen table, formatted for print) ─────────

export const buildPayrollRegisterHtml = (
  records: PayrollRecord[],
  filterMonth: string,
  filterSession: string,
  schoolName: string | undefined,
  displayOptions?: PrintDisplayOptions,
): string => {
  const { colorMode } = resolveDisplayOptions(displayOptions);
  const p = getPalette(colorMode);
  const now = fmtDateTime();

  const totalGross = records.reduce((a, r) => a + Number(r.gross_salary), 0);
  const totalDeduct = records.reduce((a, r) => a + Number(r.total_deductions), 0);
  const totalNet = records.reduce((a, r) => a + Number(r.net_salary), 0);
  const paidCount = records.filter((r) => r.payment_status === 'paid').length;
  const pendCount = records.filter((r) => r.payment_status === 'pending').length;

  const rows = records.map((r, i) => {
    const attendPct = r.working_days > 0 ? Math.round((r.present_days / r.working_days) * 100) : 0;
    return `
      <tr style="background:${i % 2 === 0 ? '#fff' : '#F8FAFF'};">
        <td class="c" style="color:#94A3B8;font-size:11px;font-weight:700;">${i + 1}</td>
        <td style="padding:8px 10px;">
          <div style="font-weight:700;color:#0F1B3D;font-size:12px;">${escapeHtml(r.employee_name ?? `#${r.employee_id}`)}</div>
          <div style="font-size:10px;color:#9CA3AF;margin-top:1px;">ID #${r.employee_code ?? r.employee_id}</div>
        </td>
        <td class="c" style="font-size:11px;color:#374151;font-weight:600;text-transform:capitalize;">${r.employee_role}</td>
        <td class="c" style="font-size:11px;color:#374151;font-weight:600;white-space:nowrap;">${monthLabel(r.month_year)}</td>
        <td class="c"><span style="font-weight:700;color:${ink('#1D4ED8', colorMode)};font-size:11px;">${fmtCurrency(r.gross_salary)}</span></td>
        <td class="c"><span style="font-weight:700;color:${ink('#DC2626', colorMode)};font-size:11px;">−${fmtCurrency(r.total_deductions)}</span></td>
        <td class="c"><span style="font-weight:800;color:${ink('#2855AE', colorMode)};font-size:12px;">${fmtCurrency(r.net_salary)}</span></td>
        <td class="c" style="font-size:11px;color:${attendPct >= 75 ? ink('#16A34A', colorMode) : ink('#DC2626', colorMode)};font-weight:700;">${r.present_days}/${r.working_days} (${attendPct}%)</td>
        <td class="c">${statusBadgeHtml(r.payment_status, colorMode)}</td>
      </tr>`;
  }).join('');

  const title = `Payroll Register${filterMonth ? ` · ${monthLabel(filterMonth)}` : ''}${filterSession ? ` · ${escapeHtml(filterSession)}` : ''}`;

  return `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"/><title>${title}</title><style>${getBaseStyles(colorMode)}</style></head>
<body><div class="page wide">
  <div class="header">
    <div class="hl">
      ${buildLogoHtml(schoolName, colorMode)}
      <div style="font-size:15px;font-weight:800;color:${p.textPrimary};letter-spacing:-0.2px;">${title}</div>
      <div style="font-size:11px;color:${p.textSecondary};margin-top:4px;">Generated: ${now}</div>
    </div>
    <div class="hr">
      <div style="font-size:32px;font-weight:900;color:${p.textPrimary};line-height:1;">${records.length}</div>
      <div style="font-size:11px;color:${p.textSecondary};margin-top:2px;">Records</div>
    </div>
  </div>

  <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-bottom:20px;">
    <div class="sc2"><div class="sk">Gross Payable</div><div class="sv" style="color:${ink('#1D4ED8', colorMode)};">${fmtCurrency(totalGross)}</div></div>
    <div class="sc2"><div class="sk">Total Deductions</div><div class="sv" style="color:${ink('#DC2626', colorMode)};">${fmtCurrency(totalDeduct)}</div></div>
    <div class="sc2"><div class="sk">Net Payable</div><div class="sv" style="color:${ink('#2855AE', colorMode)};">${fmtCurrency(totalNet)}</div></div>
    <div class="sc2"><div class="sk">Paid / Pending</div><div class="sv" style="color:#374151;">${paidCount} / ${pendCount}</div></div>
  </div>

  <div class="table-wrap">
    <table>
      <thead><tr>
        <th class="c" style="width:36px;">#</th>
        <th>Employee</th>
        <th class="c">Role</th>
        <th class="c">Month</th>
        <th class="c">Gross</th>
        <th class="c">Deductions</th>
        <th class="c">Net Salary</th>
        <th class="c">Attendance</th>
        <th class="c">Status</th>
      </tr></thead>
      <tbody>
        ${rows || `<tr><td colspan="9" style="text-align:center;padding:40px;color:#94A3B8;">No records found</td></tr>`}
      </tbody>
    </table>
  </div>

  <div class="footer"><span style="font-weight:700;color:#2855AE;">${schoolName ? escapeHtml(schoolName) : 'SkoolPillar ERP'}</span> &nbsp;·&nbsp; ${schoolName ? 'Powered by SkoolPillar ERP' : 'Payroll Management System'} &nbsp;·&nbsp; ${now}</div>
</div></body></html>`;
};
